import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import MoreStories from '../components/MoreStories';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const galleryPhotos = [
  {
    src: '/portfolio-love-blossoms.jpg',
    caption: 'A Family, Made Whole',
    location: 'Before the Limousine',
  },
  {
    src: '/blossoms-altar-ceremony.jpg',
    caption: 'The Sacred Vow',
    location: 'At The Altar',
  },
  {
    src: '/blossoms-escort-table.jpg',
    caption: 'A Welcome in Bloom',
    location: 'Calla Lily Escort Display',
  },
  {
    src: '/blossoms-first-kiss.jpg',
    caption: 'Sealed With a Kiss',
    location: 'The Ballroom Dip',
  },
  {
    src: '/blossoms-planner-table.jpg',
    caption: 'Every Detail, Watched Over',
    location: 'Behind the Magic',
  },
];

const LoveBlossomsStory = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const storyRef = useRef(null);
  const lightbox = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleViewStory = () => {
    setStoryOpen(true);
    setTimeout(() => {
      storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="Our Love Blossoms — Gaby & Michael's Wedding | Dion Magee Events"
        description="The love story of Gaby & Michael in Ridgefield, NJ. A wedding that bloomed slowly — petal by petal — captured by Sir Dion Magee Events & Weddings."
        path="/portfolio/our-love-blossoms"
        image="/portfolio-love-blossoms.jpg"
        type="article"
        article={{
          headline: "Our Love Blossoms — Gaby & Michael's Wedding",
          datePublished: '2024-09-21',
          section: 'Wedding Stories',
        }}
      />
      {/* HERO */}
      <section className="relative h-[90vh] w-full overflow-hidden">
        <img
          src="/portfolio-love-blossoms.jpg"
          alt="Our Love Blossoms"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-black/55" />

        {/* Back link */}
        <div className="absolute top-8 left-6 lg:top-12 lg:left-12 z-20">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-3 text-white/80 hover:text-[#c9a96e] transition-colors text-[11px] tracking-[0.4em] uppercase"
          >
            <ArrowLeft size={16} />
            Back to Portfolio
          </Link>
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20 lg:pb-28 px-6 text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            A Wedding Story
          </p>
          <h1
            className="font-serif italic text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl leading-[1.05]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Our Love Blossoms
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mt-10" />
          <p className="text-white/70 text-sm tracking-[0.35em] uppercase mt-6">
            Gaby &nbsp;&amp;&nbsp; Michael &nbsp;&middot;&nbsp; Ridgefield, NJ
          </p>
        </div>
      </section>

      {/* TOP GALLERY - 3 photos */}
      <section className="bg-[#0a0a0a] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-5">The Chapters</p>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl">
              A Love That Bloomed Slowly
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {galleryPhotos.slice(0, 3).map((p, i) => (
              <figure
                key={i}
                data-testid={`blossoms-photo-${i}`}
                onClick={() => lightbox.openAt(i)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 1).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* GOLD ELEGANT "VIEW STORY" CTA */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 px-6">
        <div className="pointer-events-none absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-[#c9a96e]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>

          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">A Love Letter to Two Hearts</p>
          <h3
            className="font-serif italic text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-snug"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step into Gaby &amp; Michael&rsquo;s day &mdash; a love that bloomed, petal by petal.
          </h3>

          <button
            type="button"
            onClick={handleViewStory}
            data-testid="view-story-button"
            className="group relative inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent overflow-hidden transition-colors duration-500 hover:bg-[#c9a96e]/10"
          >
            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />
            <span
              className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em] relative z-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              View Story
            </span>
          </button>

          <div className="flex items-center justify-center gap-6 mt-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
        </div>
      </section>

      {/* STORY NARRATIVE */}
      <section
        ref={storyRef}
        className={`bg-[#0a0a0a] px-6 lg:px-12 overflow-hidden transition-all duration-700 ease-out ${
          storyOpen ? 'max-h-[6000px] opacity-100 py-20 lg:py-28' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-6">The Story</p>
            <h2
              className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Where Two Worlds Became One
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
          </div>

          <div
            className="space-y-7 text-white/80 text-lg leading-[1.9] font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="blossoms-story-text"
          >
            <p className="first-letter:font-serif first-letter:italic first-letter:text-[#c9a96e] first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">
              Long before Gaby ever met Michael, she had already learned how
              to love beyond herself. Two small hands had taught her that &mdash;
              a son with a kiss like a benediction, a daughter with eyes the color
              of new beginnings. By the time Michael walked quietly into her
              life, she had built a small, perfect world. He did not try to
              change it. He simply stepped inside it, and made it brighter.
            </p>

            <p>
              They fell in love the way calla lilies open &mdash; slowly, deliberately,
              petal by careful petal. There were ordinary Tuesdays and small
              kindnesses. There were car rides through Ridgefield with the
              children asleep in the back, and long conversations that turned
              the dark into something gentle. Somewhere along that quiet path,
              they decided forever.
            </p>

            <p>
              On the morning of the wedding, the children were the first to
              find her. Her little boy reached for her face with a kiss; her
              daughter peered upward in a halo of blue and white. Michael
              knelt beside them in the late summer light, and for one suspended
              moment, every vow that would be spoken later that day had already
              been silently kept.
            </p>

            <p>
              Inside the church, beneath stained glass and the steady voice of
              the priest, Gaby and Michael stood hand in hand &mdash; she in a tiered
              ivory gown, he with a sprig of lily of the valley at his lapel.
              They whispered the ancient words as though discovering them for
              the first time, and the world held its breath with them.
            </p>

            <p>
              The reception bloomed in cobalt and cream. Tall calla lilies
              arched above tables of sapphire goblets and floating candlelight.
              Guests found their names upon a mirrored table beneath a crown
              of pale hydrangeas &mdash; each welcome arranged like a love letter,
              each detail studied and adored.
            </p>

            <p>
              And then, in the warm gold light of the ballroom, Michael took
              Gaby by the waist and dipped her into a kiss the room would not
              soon forget &mdash; the bride&rsquo;s veil sweeping the floor, the
              groom&rsquo;s hand gentle at her back. The children clapped. The
              crystal sang. Love, by then, had bloomed entirely.
            </p>

            <p className="text-center text-[#c9a96e] italic pt-6">
              &mdash; And like the lilies they chose, their love continues to open softly: tender at the edges, luminous at its center.
            </p>
          </div>

          {/* BOTTOM GALLERY - 2 more photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-20 max-w-4xl mx-auto">
            {galleryPhotos.slice(3).map((p, i) => (
              <figure
                key={i + 3}
                data-testid={`blossoms-photo-${i + 3}`}
                onClick={() => lightbox.openAt(i + 3)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 4).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Closing */}
          <div className="text-center mt-20">
            <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-8" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">An Original Production by</p>
            <p
              className="font-serif italic text-white text-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Dion Magee Events &amp; Weddings
            </p>
            <Link
              to="/#contact"
              data-testid="story-cta-enquire"
              className="inline-block mt-10 px-10 py-4 border border-[#c9a96e] text-[#e8d5a8] text-[11px] tracking-[0.4em] uppercase hover:bg-[#c9a96e] hover:text-black transition-colors duration-500"
            >
              Begin Your Own Story
            </Link>
          </div>
        </div>
      </section>

      <MoreStories currentSlug="our-love-blossoms" />
      <Marquee />
      <Footer />
      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={galleryPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </div>
  );
};

export default LoveBlossomsStory;
